import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Tooltip,
  Stack,
  TextField,
  Checkbox,
  FormControlLabel,
  Button
} from '@mui/material';
import { toast } from 'react-toastify';

import MUITypography from '../../Components/MUITypography';
import Loader from '../../Components/Loader';
import StorageFactory from '../../Util/StorageFactory';

import {
  ACCESS_RESTRICTED_MESSAGE,
  ROUTE_ENQUIRY_DETAILS,
  SESSION_USER_ID_KEY,
  SESSION_USER_ROLES_PERMISSIONS,
  STATUS_CONFIRMED
} from '../../Util/VMConstants';

import {
  align_left,
  align_right,
  align_row
} from '../../Styles/VMStyles';

import { API_GET_ALL_CONFIRMED_ENQUIRY_DATA, API_GET_OTHER_THAN_CONFIRM_ENQUIRY_DATA } from '../../Util/URLConstants';
import { checkIsPermissionExist, isEmpty, isTodayDate, renderSNo } from '../../Util/UtilHelper';
import MuiDatagrid from '../../Components/MUIDataGrid';
import StatusButton from '../../Components/StatusButton';
import NoPermission from '../../Components/NoPermission';

const normalizeBookingFor = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value != null && value !== '') return [String(value)];
  return [];
};

const formatEventSubVenuesWithBooking = (enq) => {
  const venues = Array.isArray(enq.event_sub_venues) ? enq.event_sub_venues : [];
  const subVenueIds = Array.isArray(enq.event_subvenue_ids)
    ? enq.event_subvenue_ids.map((id) => String(id))
    : [];
  const bookingFors = Array.isArray(enq.event_booking_fors) ? enq.event_booking_fors : [];

  const venueById = venues.reduce((acc, venue) => {
    const id = String(venue.sub_venue_id ?? '');
    if (id) acc[id] = venue;
    return acc;
  }, {});

  const isNestedBookingFors = bookingFors.some((item) => Array.isArray(item));

  const getRootBookingAt = (index) => {
    const entry = bookingFors[index];
    if (Array.isArray(entry)) return entry.filter(Boolean);
    return normalizeBookingFor(entry);
  };

  // API pairs booking slots with sub-venue via event_subvenue_ids[i] + event_booking_fors[i],
  // not by the order of event_sub_venues (which can differ).
  if (subVenueIds.length > 0) {
    return subVenueIds
      .map((venueId, index) => {
        const venue = venueById[venueId] || {};
        const nestedBooking = normalizeBookingFor(venue.event_booking_for);
        let rootBooking = getRootBookingAt(index);

        if (
          !isNestedBookingFors &&
          subVenueIds.length === 1 &&
          rootBooking.length <= 1 &&
          bookingFors.length > 1
        ) {
          rootBooking = normalizeBookingFor(bookingFors);
        }

        return {
          name: venue.sub_venue_name || '',
          bookingFor: nestedBooking.length ? nestedBooking : rootBooking,
        };
      })
      .filter((v) => v.name || v.bookingFor.length);
  }

  return venues
    .map((venue, index) => {
      const nestedBooking = normalizeBookingFor(venue.event_booking_for);
      const rootBooking = getRootBookingAt(index);

      return {
        name: venue.sub_venue_name || '',
        bookingFor: nestedBooking.length ? nestedBooking : rootBooking,
      };
    })
    .filter((v) => v.name || v.bookingFor.length);
};

const mapEnquiryVenueFields = (enq) => ({
  event_sub_venues: formatEventSubVenuesWithBooking(enq),
});

const renderSubVenueColumn = (venueRows) => {
  if (!venueRows?.length) {
    return <MUITypography label="-" />;
  }

  return (
    <Box component="ul" sx={{ pl: 2, m: 0 }}>
      {venueRows.map((venue, index) => {
        const bookingLabel = venue.bookingFor?.length
          ? venue.bookingFor.join(', ')
          : null;
        const tooltip = [venue.name, bookingLabel].filter(Boolean).join(' — ');

        return (
          <li key={index}>
            <Tooltip title={tooltip || '-'}>
              <Box>
                {venue.name ? (
                  <MUITypography label={venue.name} sx={align_left} />
                ) : null}
                {bookingLabel ? (
                  <MUITypography
                    label={bookingLabel}
                    sx={{
                      ...align_left,
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      lineHeight: 1.3,
                    }}
                  />
                ) : null}
                {!venue.name && !bookingLabel ? (
                  <MUITypography label="-" sx={align_left} />
                ) : null}
              </Box>
            </Tooltip>
          </li>
        );
      })}
    </Box>
  );
};

const VMEvents = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const typeFromQuery = query.get('type') || 'events';
  const dateFromQuery = query.get('date');

  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS);

  /* ---------------- STATE ---------------- */
  const [rows, setRows] = useState([]);
  const [rows1, setRows1] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalCount1, setTotalCount1] = useState(0);

  const [todayOnly, setTodayOnly] = useState(!isEmpty(dateFromQuery));
  const [startDate, setStartDate] = useState(null); // YYYY-MM-DD
  const [endDate, setEndDate] = useState(null);     // YYYY-MM-DD

useEffect(()=>{

  if(!isEmpty(dateFromQuery)){
    if(isTodayDate(dateFromQuery)){
    setTodayOnly(true);
    }else{
    setTodayOnly(false);
    }

    setStartDate(dateFromQuery);
    setEndDate(dateFromQuery);
  }else{
     setTodayOnly(false);
     setStartDate(null);
     setEndDate(null);
  }
},[dateFromQuery])



  /* ---------------- FETCH DATA ---------------- */
const fetchEvents = useCallback(async () => {
  try {
    setLoading(true);

    const params = new URLSearchParams({
      user_id: userID,
      page: page,
      no_of_rows: pageSize,
    });

    if (typeFromQuery === 'events') {
      params.append('status', 'Confirmed');
    }

    if (todayOnly) {
      const baseDate = dateFromQuery || dayjs().format('YYYY-MM-DD');
      params.append('from_date', baseDate);
      params.append('to_date', baseDate);
    }

    if (!todayOnly && startDate && endDate) {
      params.append('from_date', startDate);
      params.append('to_date', endDate);
    }

    const type = typeFromQuery === 'enquiries' ? 'enquiries' : 'events';
    params.append('type', type);
    params.append('status', STATUS_CONFIRMED);

    const res = await fetch(`${API_GET_ALL_CONFIRMED_ENQUIRY_DATA}?${params.toString()}`);
    const resp = await res.json();

    if (resp.status_code === 200 && Array.isArray(resp.data)) {
      const formattedRows = resp.data.map((enq, idx) => ({
        s_no: idx + 1 + page * pageSize,
        id: enq.appointment_id,
        appointment_id: enq.appointment_id,
        name: enq.name || '',
        phone: `${enq.country_code || ''} ${enq.phone_no || ''}`.trim(),
        appointment_status: enq.appointment_status,
        event_date: Array.isArray(enq.appointment_date)
          ? enq.appointment_date.map((d) => dayjs(d).format('DD/MM/YYYY')).join(', ')
          : '',
        ...mapEnquiryVenueFields(enq),
      }));

      setRows(formattedRows);
      setTotalCount(resp.totalCount || 0);
    } else {
      toast.error(resp.message || 'Failed to load data');
      setRows([]);
    }
  } catch (err) {
    console.error(err);
    toast.error('Something went wrong while loading data');
    setRows([]);
  } finally {
    setLoading(false);
  }
}, [userID, page, pageSize, todayOnly, startDate, endDate, typeFromQuery, dateFromQuery]);


const fetchEvents1 = useCallback(async () => {
  try {
    setLoading(true);

    const params = new URLSearchParams({
      user_id: userID,
      page: page,
      no_of_rows: pageSize,
    });

    if (typeFromQuery === 'events') {
      params.append('status', 'Confirmed');
    }

    if (todayOnly) {
      const baseDate = dateFromQuery || dayjs().format('YYYY-MM-DD');
      params.append('from_date', baseDate);
      params.append('to_date', baseDate);
    }

    if (!todayOnly && startDate && endDate) {
      params.append('from_date', startDate);
      params.append('to_date', endDate);
    }

    const type = typeFromQuery === 'enquiries' ? 'enquiries' : 'events';
    params.append('type', type);

    const res = await fetch(`${API_GET_OTHER_THAN_CONFIRM_ENQUIRY_DATA}?${params.toString()}`);
    const resp = await res.json();

    if (resp.status_code === 200 && Array.isArray(resp.data)) {
      const formattedRows = resp.data.map((enq, idx) => ({
        s_no: idx + 1 + page * pageSize,
        id: enq.appointment_id,
        appointment_id: enq.appointment_id,
        name: enq.name || '',
        phone: `${enq.country_code || ''} ${enq.phone_no || ''}`.trim(),
        appointment_status: enq.appointment_status,
        event_date: Array.isArray(enq.appointment_date)
          ? enq.appointment_date.map((d) => dayjs(d).format('DD/MM/YYYY')).join(', ')
          : '',
        ...mapEnquiryVenueFields(enq),
      }));

      setRows1(formattedRows);
      setTotalCount1(resp.totalCount || 0);
    } else {
      toast.error(resp.message || 'Failed to load data');
      setRows1([]);
    }
  } catch (err) {
    console.error(err);
    toast.error('Something went wrong while loading data');
    setRows1([]);
  } finally {
    setLoading(false);
  }
}, [userID, page, pageSize, todayOnly, startDate, endDate, typeFromQuery, dateFromQuery]);

/* ---------------- EFFECTS ---------------- */
  useEffect(() => {
    setPage(0);
  }, [todayOnly, startDate, endDate, typeFromQuery]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);


  useEffect(() => {
    fetchEvents1();
  }, [fetchEvents1]);


  /* ---------------- COLUMNS ---------------- */
  const columns = [
    renderSNo(),
    {
      field: 'appointment_id',
      headerName: typeFromQuery === 'events' ? 'Event ID' : 'Enquiry ID',
      flex: 1,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <MUITypography
            sx={{ ...align_left,height: '100%',
              cursor: 'pointer',
              color: 'primary.main',
              textDecoration: 'underline',
            }}
            onClick={() =>
              navigate('/' + ROUTE_ENQUIRY_DETAILS, {
                state: { enquiry_id: params.value },
              })
            }
          >
            {params.value}
          </MUITypography>
        </Tooltip>
      ),
    },
    { field: 'name', headerName: 'Name', flex: 1 ,
      renderCell: (params) => (
        <Tooltip title= {params.value}>
          <MUITypography sx={align_left}  >
            {params.value}
          </MUITypography>
        </Tooltip>
      ),
    },
    { field: 'phone', headerName: 'Phone No', flex: 1,
      renderCell: (params) => (
        <Tooltip title= {params.value}>
          <MUITypography sx={align_left}  >
            {params.value}
          </MUITypography>
        </Tooltip>
      ),
    },
    { field: 'event_date', headerName: 'Date', flex: 1 ,
      renderCell: (params) => (
        <Tooltip title= {params.value}>
          <MUITypography sx={align_left}  >
            {params.value}
          </MUITypography>
        </Tooltip>
      ),},
    {
      field: 'event_sub_venues',
      headerName: 'Sub Venue',
      flex: 1.5,
      sortable: false,
      renderCell: (params) => renderSubVenueColumn(params.value),
    },
    {
      field: 'appointment_status',
      headerName: 'Status',
      flex: 1,
      sortable: false,
      renderCell: (params) => <StatusButton status={params.value} />,
    },

  
  ];

  /* ---------------- UI ---------------- */
  return (
    <Box sx={{ p: 2 ,...(location?.pathname?.includes('events') ? { ml: { md: 0 } } : {}) }}>
      
      <Box sx={align_row}>
        <Box sx={align_left}>
          <MUITypography
            // label={typeFromQuery === 'enquiries' ? 'Enquiries' : 'Events'}
           label = 'Confirmed Events' 
            variant="h4"
            sx={{ fontWeight: 700 }}
          />
        </Box>

        <Box sx={align_right}>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={todayOnly}
                  onChange={(e) => {
                    setTodayOnly(e.target.checked);
                    setStartDate(null);
                    setEndDate(null);
                  }}
                />
              }
              label="Today"
            />

            {!todayOnly && (
              <>
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={startDate || ''}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={endDate || ''}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </>
            )}

            <Button
              variant="outlined"
              onClick={() => {
                setTodayOnly(false);
                setStartDate(null);
                setEndDate(null);
              }}
            >
              Reset
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* 🔥 Reusable MuiDatagrid */}
     {checkIsPermissionExist(VM_PERMISSIONS, 'VM002') ? (
      <MuiDatagrid
        rows={rows}
        columns={columns}
        loading={loading}
        page={page}
        pageSize={pageSize}
        rowCount={totalCount}
        paginationMode="server"
        onPageChange={setPage}
        paginationModel={{ page, pageSize }}       
        getRowHeight={() => 'auto'}              
        onPaginationModelChange={(model) => {                    
          if (model.page !== page) setPage(model.page);
          if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
            setPage(0);                                  
          }
        }}

        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(0);
        }}
        getRowId={(row) => row.id}
      />
) : <NoPermission message= {ACCESS_RESTRICTED_MESSAGE} />

}



          {/* 🔥 Reusable MuiDatagrid */}

     {checkIsPermissionExist(VM_PERMISSIONS, 'VM002') ? (
     <>
           <MUITypography
            label={'Enquiries'}
            variant="h4"
            sx={{ fontWeight: 700 ,mt:5,mb:1}}
          />    
      <MuiDatagrid
        rows={rows1}
        columns={columns}
        loading={loading}
        page={page}
        pageSize={pageSize}
        rowCount={totalCount1}
        paginationMode="server"
        onPageChange={setPage}
        paginationModel={{ page, pageSize }}
        getRowHeight={() => 'auto'}
        onPaginationModelChange={(model) => {                    
          if (model.page !== page) setPage(model.page);
          if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
            setPage(0);                                  
          }
        }}

        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(0);
        }}
        getRowId={(row) => row.id}
      />
     </>
) : null

}

    </Box>
  );
};

export default VMEvents;
